import {
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/solid';
import {
  MagnifyingGlassIcon,
  PlayIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { twMerge } from 'tailwind-merge';

import { DashedTag, SearchBar } from '@/components/chariot-ui';
import { Loader } from '@/components/Loader';
import { Popover } from '@/components/Popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMy } from '@/hooks/useMy';
import { useScroll } from '@/hooks/useScroll';
import type { Asset } from '@/types';
import { useEnrichmentPlugins } from '@/sections/customerManagement/components/masterDashboard/hooks/useEnrichmentPlugins';
import { useRunEnrichment } from '@/sections/customerManagement/components/masterDashboard/hooks/useEnrichmentMutations';

interface ClientRunTabProps {
  client: string;
}

const MODE_ALL = 'all';
const MODE_OPTIONS = [
  { value: MODE_ALL, label: 'All plugins (default)' },
  { value: 'active', label: 'Active only' },
  { value: 'passive', label: 'Passive only' },
];

interface PluginDef {
  id: string;
  name: string;
  mode: 'passive' | 'active';
  category: 'domain' | 'cidr' | 'api-key';
  needsApiKey: boolean;
}

// Authoritative plugin catalog — mirrors pluginNames in pius/pkg/lib/discovery.go
const ALL_PLUGINS: PluginDef[] = [
  // Domain plugins (passive)
  { id: 'crt-sh', name: 'crt.sh', mode: 'passive', category: 'domain', needsApiKey: false },
  { id: 'apollo', name: 'Apollo', mode: 'passive', category: 'domain', needsApiKey: true },
  { id: 'github-org', name: 'GitHub Org', mode: 'passive', category: 'domain', needsApiKey: true },
  { id: 'gleif', name: 'GLEIF', mode: 'passive', category: 'domain', needsApiKey: false },
  { id: 'passive-dns', name: 'Passive DNS', mode: 'passive', category: 'domain', needsApiKey: false },
  { id: 'reverse-whois', name: 'Reverse WHOIS', mode: 'passive', category: 'domain', needsApiKey: false },
  { id: 'whois', name: 'WHOIS', mode: 'passive', category: 'domain', needsApiKey: false },
  { id: 'urlscan', name: 'URLScan', mode: 'passive', category: 'domain', needsApiKey: false },
  { id: 'wayback', name: 'Wayback Machine', mode: 'passive', category: 'domain', needsApiKey: false },
  { id: 'wikidata', name: 'Wikidata', mode: 'passive', category: 'domain', needsApiKey: false },
  { id: 'google-dorks', name: 'Google Dorks', mode: 'passive', category: 'domain', needsApiKey: false },
  { id: 'reverse-ip', name: 'Reverse IP', mode: 'passive', category: 'domain', needsApiKey: false },
  { id: 'otx-alienvault', name: 'OTX AlienVault', mode: 'passive', category: 'domain', needsApiKey: false },
  // Domain plugins (active)
  { id: 'dns-brute', name: 'DNS Brute Force', mode: 'active', category: 'domain', needsApiKey: false },
  { id: 'dns-zone-transfer', name: 'DNS Zone Transfer', mode: 'active', category: 'domain', needsApiKey: false },
  { id: 'doh-enum', name: 'DoH Enumeration', mode: 'active', category: 'domain', needsApiKey: false },
  { id: 'dns-permutation', name: 'DNS Permutation', mode: 'active', category: 'domain', needsApiKey: false },
  { id: 'favicon-hash', name: 'Favicon Hash', mode: 'active', category: 'domain', needsApiKey: false },
  // CIDR plugins (passive)
  { id: 'asn-bgp', name: 'ASN BGP', mode: 'passive', category: 'cidr', needsApiKey: false },
  { id: 'reverse-rir', name: 'Reverse RIR', mode: 'passive', category: 'cidr', needsApiKey: false },
  { id: 'edgar', name: 'EDGAR', mode: 'passive', category: 'cidr', needsApiKey: false },
  { id: 'arin', name: 'ARIN', mode: 'passive', category: 'cidr', needsApiKey: false },
  { id: 'ripe', name: 'RIPE', mode: 'passive', category: 'cidr', needsApiKey: false },
  { id: 'lacnic', name: 'LACNIC', mode: 'passive', category: 'cidr', needsApiKey: false },
  { id: 'apnic', name: 'APNIC', mode: 'passive', category: 'cidr', needsApiKey: false },
  { id: 'afrinic', name: 'AfriNIC', mode: 'passive', category: 'cidr', needsApiKey: false },
  // API-key plugins (passive — except censys which is active)
  { id: 'shodan', name: 'Shodan', mode: 'passive', category: 'api-key', needsApiKey: true },
  { id: 'dnsdb', name: 'DNSDB', mode: 'passive', category: 'api-key', needsApiKey: true },
  { id: 'crunchbase', name: 'Crunchbase', mode: 'passive', category: 'api-key', needsApiKey: true },
  { id: 'opencorporates', name: 'OpenCorporates', mode: 'passive', category: 'api-key', needsApiKey: true },
  { id: 'proxycurl', name: 'Proxycurl', mode: 'passive', category: 'api-key', needsApiKey: true },
  { id: 'diffbot', name: 'Diffbot', mode: 'passive', category: 'api-key', needsApiKey: true },
  { id: 'securitytrails', name: 'SecurityTrails', mode: 'passive', category: 'api-key', needsApiKey: true },
  { id: 'virustotal', name: 'VirusTotal', mode: 'passive', category: 'api-key', needsApiKey: true },
  { id: 'binaryedge', name: 'BinaryEdge', mode: 'passive', category: 'api-key', needsApiKey: true },
  { id: 'censys-org', name: 'Censys', mode: 'active', category: 'api-key', needsApiKey: true },
  { id: 'viewdns', name: 'ViewDNS', mode: 'passive', category: 'api-key', needsApiKey: true },
];

// Map pius_* integration IDs to short plugin names for hasMasterKey lookup
const PIUS_PREFIX_TO_SHORT: Record<string, string> = {
  pius_shodan: 'shodan',
  pius_dnsdb: 'dnsdb',
  pius_crunchbase: 'crunchbase',
  pius_opencorporates: 'opencorporates',
  pius_proxycurl: 'proxycurl',
  pius_diffbot: 'diffbot',
  pius_github: 'github-org',
  pius_securitytrails: 'securitytrails',
  pius_virustotal: 'virustotal',
  pius_binaryedge: 'binaryedge',
  pius_apollo: 'apollo',
  pius_censys: 'censys-org',
  pius_viewdns: 'viewdns',
};

interface PluginParam {
  name: string;
  description: string;
  defaultValue?: string;
  type: 'string' | 'int' | 'bool';
}

const PLUGIN_PARAMS: Record<
  string,
  { mode: 'active' | 'passive'; label: string; params: PluginParam[] }
> = {
  'doh-enum': {
    mode: 'active',
    label: 'DoH Enumeration',
    params: [
      {
        name: 'doh_servers',
        description: 'Comma-separated DoH servers',
        type: 'string',
      },
      {
        name: 'doh_gateways',
        description: 'Comma-separated DoH gateways',
        type: 'string',
      },
      {
        name: 'doh_deploy_gateways',
        description: 'Deploy gateways',
        type: 'bool',
        defaultValue: 'false',
      },
    ],
  },
  'dns-brute': {
    mode: 'active',
    label: 'DNS Brute Force',
    params: [
      {
        name: 'dns_brute_concurrency',
        description: 'Concurrency level',
        type: 'int',
        defaultValue: '100',
      },
    ],
  },
  'google-dorks': {
    mode: 'passive',
    label: 'Google Dorks',
    params: [
      {
        name: 'google_dorks_max_subsidiaries',
        description: 'Max subsidiaries to dork',
        type: 'int',
        defaultValue: '10',
      },
    ],
  },
};

function isPluginConfigVisible(
  pluginName: string,
  pluginMode: 'active' | 'passive',
  currentMode: string,
  selectedPlugins: string[]
): boolean {
  if (selectedPlugins.length > 0) return selectedPlugins.includes(pluginName);
  if (currentMode === '' || currentMode === 'all') return true;
  return currentMode === pluginMode;
}

function CollapsiblePluginConfig({
  pluginName,
  label,
  params,
  values,
  onChange,
}: {
  pluginName: string;
  label: string;
  params: PluginParam[];
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-chariot-stroke rounded border">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="hover:bg-chariot-background-secondary flex w-full items-center gap-2 px-3 py-2 text-left transition-colors"
      >
        <ChevronRightIcon
          className={twMerge(
            'size-4 shrink-0 transition-transform duration-200',
            isOpen && 'rotate-90'
          )}
        />
        <span className="text-chariot-text-primary text-sm font-medium">
          {label}
        </span>
        <span className="text-chariot-text-secondary text-xs">
          ({pluginName})
        </span>
      </button>
      <div
        className={twMerge(
          'grid transition-[grid-template-rows,opacity] duration-300 ease-in-out',
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-3 px-3 pt-1 pb-3">
            {params.map(param => (
              <div key={param.name} className="flex items-center gap-4">
                <div className="flex min-w-40 max-w-56 shrink-0 items-center gap-2">
                  <label className="text-chariot-text-primary text-sm font-medium">
                    {param.name}
                  </label>
                </div>
                <div className="flex-1">
                  {param.type === 'bool' ? (
                    <Select
                      value={
                        values[param.name] || param.defaultValue || 'false'
                      }
                      onValueChange={val => onChange(param.name, val)}
                    >
                      <SelectTrigger className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-[2px] border focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="false">Disabled</SelectItem>
                        <SelectItem value="true">Enabled</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : param.type === 'int' ? (
                    <input
                      type="number"
                      min="0"
                      step="1"
                      placeholder={param.defaultValue || ''}
                      value={values[param.name] || ''}
                      onChange={e => onChange(param.name, e.target.value)}
                      className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary block w-full rounded-[2px] border p-1.5 pl-3 outline-0 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-hidden"
                    />
                  ) : (
                    <input
                      type="text"
                      placeholder={
                        param.defaultValue || `Enter ${param.name}...`
                      }
                      value={values[param.name] || ''}
                      onChange={e => onChange(param.name, e.target.value)}
                      className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary block w-full rounded-[2px] border p-1.5 pl-3 outline-0 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-hidden"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export const ClientRunTab: React.FC<ClientRunTabProps> = ({ client }) => {
  const { data: enrichmentPlugins } = useEnrichmentPlugins();
  const { mutateAsync: runEnrichment, isPending } = useRunEnrichment();

  const [selectedPlugins, setSelectedPlugins] = useState<string[]>([]);
  const [pluginDropdownOpen, setPluginDropdownOpen] = useState(false);
  const [pluginSearch, setPluginSearch] = useState('');
  const [mode, setMode] = useState(MODE_ALL);
  const [pluginConfig, setPluginConfig] = useState<Record<string, string>>({});
  const [selectedTargetMap, setSelectedTargetMap] = useState<Map<string, string>>(new Map());
  const [targetSearch, setTargetSearch] = useState('');

  const targetListRef = useRef<HTMLUListElement>(null);
  const {
    data: rawTargets,
    status: targetsStatus,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMy(
    {
      resource: 'asset',
      query: {
        node: {
          labels: ['Asset'],
          filters: [
            {
              field: '',
              operator: 'OR' as const,
              value: [
                { field: 'class', operator: '=' as const, value: 'domain' },
                { field: 'class', operator: '=' as const, value: 'tld' },
              ],
            },
            ...(targetSearch
              ? [{ field: 'key', operator: 'CONTAINS' as const, value: targetSearch }]
              : []),
          ],
        },
        limit: 50,
      },
    },
    { doNotImpersonate: true, email: client }
  );
  useScroll(targetListRef, fetchNextPage, hasNextPage);

  const masterKeyMap = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const ep of enrichmentPlugins ?? []) {
      const shortName = PIUS_PREFIX_TO_SHORT[ep.plugin];
      if (shortName) map.set(shortName, ep.hasMasterKey);
    }
    return map;
  }, [enrichmentPlugins]);

  const allPlugins = ALL_PLUGINS;

  const filteredPluginOptions = useMemo(() => {
    if (!pluginSearch) return allPlugins;
    const q = pluginSearch.toLowerCase();
    return allPlugins.filter(
      p => p.name.toLowerCase().includes(q) || p.id.toLowerCase().includes(q)
    );
  }, [allPlugins, pluginSearch]);

  const isPluginDisabledByMode = (plugin: PluginDef) => {
    if (mode === MODE_ALL) return false;
    return plugin.mode !== mode;
  };

  const handleModeChange = (newMode: string) => {
    setMode(newMode);
    if (newMode !== MODE_ALL) {
      setSelectedPlugins(prev =>
        prev.filter(id => {
          const p = ALL_PLUGINS.find(pl => pl.id === id);
          return p ? p.mode === newMode : false;
        })
      );
    }
  };

  const targetOptions = useMemo(() => {
    if (!rawTargets?.length) return [];
    return (rawTargets as Asset[]).map(a => ({
      value: a.key,
      label: a.dns || a.name || a.key,
    }));
  }, [rawTargets]);

  const visiblePluginSections = useMemo(
    () =>
      Object.entries(PLUGIN_PARAMS).filter(([pluginName, config]) =>
        isPluginConfigVisible(
          pluginName,
          config.mode,
          mode,
          selectedPlugins
        )
      ),
    [mode, selectedPlugins]
  );

  const removeTarget = (key: string) => {
    setSelectedTargetMap(prev => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  };

  const handlePluginConfigChange = (name: string, value: string) => {
    setPluginConfig(prev => ({ ...prev, [name]: value }));
  };

  const togglePlugin = (pluginId: string) => {
    setSelectedPlugins(prev =>
      prev.includes(pluginId)
        ? prev.filter(p => p !== pluginId)
        : [...prev, pluginId]
    );
  };

  const handleRun = async () => {
    const config: Record<string, string> = {};
    config.mode = mode || 'all';
    for (const [key, value] of Object.entries(pluginConfig)) {
      if (value) config[key] = value;
    }

    if (selectedPlugins.length > 0) {
      const allIds = ALL_PLUGINS.map(p => p.id);
      const disabled = allIds.filter(id => !selectedPlugins.includes(id));
      if (disabled.length > 0) {
        config.disable = disabled.join(',');
      }
    }

    try {
      const targetKeys = Array.from(selectedTargetMap.keys());
      await runEnrichment({
        client,
        targets: targetKeys.length > 0 ? targetKeys : undefined,
        config: Object.keys(config).length > 0 ? config : undefined,
      });
    } catch (err) {
      const error = err as Error;
      toast.error(error.name || 'Failed to run enrichment', {
        description: error.message || undefined,
        duration: 8000,
      });
    }
  };

  const pluginTriggerLabel = (() => {
    if (selectedPlugins.length === 0) return 'All plugins (default)';
    if (selectedPlugins.length <= 2) {
      return selectedPlugins
        .map(id => allPlugins.find(p => p.id === id)?.name ?? id)
        .join(', ');
    }
    return `${selectedPlugins
      .slice(0, 2)
      .map(id => allPlugins.find(p => p.id === id)?.name ?? id)
      .join(', ')} +${selectedPlugins.length - 2} more`;
  })();

  const enabledPlugins = allPlugins.filter(p => !isPluginDisabledByMode(p));
  const allEnabledSelected =
    enabledPlugins.length > 0 &&
    enabledPlugins.every(p => selectedPlugins.includes(p.id));

  const effectivePluginCount =
    selectedPlugins.length || allPlugins.length;
  const isPreseed = selectedTargetMap.size === 0;

  return (
    <div className="flex flex-col gap-6">
      {/* Plugin Selection */}
      <section>
        <h4 className="text-chariot-text-primary mb-2 text-sm font-medium">
          Plugins
        </h4>
        <Popover
          open={pluginDropdownOpen}
          setOpen={setPluginDropdownOpen}
          matchContainerWidth
          className="p-0"
          placement="bottom-start"
          container={
            <button
              type="button"
              onClick={() => setPluginDropdownOpen(!pluginDropdownOpen)}
              className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary flex w-full items-center justify-between rounded-[2px] border p-1.5 pr-2 pl-3 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-hidden"
            >
              <span
                className={
                  selectedPlugins.length === 0
                    ? 'text-chariot-text-secondary'
                    : 'text-chariot-text-primary'
                }
              >
                {pluginTriggerLabel}
              </span>
              <ChevronDownIcon className="text-chariot-text-secondary size-4 shrink-0" />
            </button>
          }
        >
          <div className="flex w-full flex-col">
            <div className="border-chariot-stroke border-b p-2">
              <div className="border-chariot-stroke bg-chariot-background-primary flex items-center gap-1.5 rounded border px-2 py-1">
                <MagnifyingGlassIcon className="text-chariot-text-secondary size-3.5 shrink-0" />
                <input
                  type="text"
                  placeholder="Search plugins..."
                  value={pluginSearch}
                  onChange={e => setPluginSearch(e.target.value)}
                  className="text-chariot-text-primary placeholder:text-chariot-text-secondary w-full bg-transparent text-sm outline-hidden"
                  onClick={e => e.stopPropagation()}
                />
              </div>
            </div>
            <div className="border-chariot-stroke flex items-center justify-between border-b px-3 py-1.5">
              <button
                type="button"
                onClick={
                  allEnabledSelected
                    ? () => setSelectedPlugins([])
                    : () =>
                        setSelectedPlugins(
                          enabledPlugins.map(p => p.id)
                        )
                }
                className="text-chariot-primary text-xs hover:underline"
              >
                {allEnabledSelected ? 'Clear All' : 'Select All'}
              </button>
              {selectedPlugins.length > 0 && (
                <span className="text-chariot-text-secondary text-xs">
                  {selectedPlugins.length} selected
                </span>
              )}
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: 280 }}>
              {filteredPluginOptions.length === 0 ? (
                <div className="text-chariot-text-secondary px-3 py-2 text-sm">
                  No plugins found
                </div>
              ) : (
                filteredPluginOptions.map(plugin => {
                  const isChecked = selectedPlugins.includes(plugin.id);
                  const disabled = isPluginDisabledByMode(plugin);
                  const hasMasterKey = masterKeyMap.get(plugin.id);
                  return (
                    <button
                      key={plugin.id}
                      type="button"
                      onClick={() => !disabled && togglePlugin(plugin.id)}
                      className={twMerge(
                        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                        disabled
                          ? 'cursor-not-allowed opacity-40'
                          : 'hover:bg-chariot-background-secondary',
                        isChecked
                          ? 'text-chariot-text-primary'
                          : 'text-chariot-text-secondary'
                      )}
                    >
                      <span
                        className={twMerge(
                          'flex size-4 shrink-0 items-center justify-center rounded border',
                          disabled
                            ? 'border-chariot-stroke bg-chariot-background-secondary'
                            : isChecked
                              ? 'bg-chariot-primary border-chariot-primary'
                              : 'border-chariot-stroke'
                        )}
                      >
                        {isChecked && !disabled && (
                          <svg
                            viewBox="0 0 10 8"
                            className="size-2.5 text-white"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path d="M1 4l3 3 5-6" />
                          </svg>
                        )}
                      </span>
                      <div className="flex min-w-0 flex-1 items-center gap-1.5">
                        <span
                          className={twMerge(
                            'truncate',
                            disabled && 'line-through'
                          )}
                        >
                          {plugin.name}
                        </span>
                        <span className="text-chariot-text-tertiary text-[10px]">
                          {plugin.mode}
                        </span>
                        {plugin.needsApiKey && hasMasterKey === false && (
                          <span className="shrink-0 rounded bg-yellow-500/15 px-1.5 py-0.5 text-[10px] font-medium text-yellow-600">
                            No key
                          </span>
                        )}
                        {plugin.needsApiKey && hasMasterKey === true && (
                          <span className="shrink-0 rounded bg-green-500/15 px-1.5 py-0.5 text-[10px] font-medium text-green-600">
                            Key
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </Popover>
      </section>

      {/* Mode Selection */}
      <section>
        <h4 className="text-chariot-text-primary mb-2 text-sm font-medium">
          Mode
        </h4>
        <Select value={mode} onValueChange={handleModeChange}>
          <SelectTrigger className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-[2px] border text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MODE_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </section>

      {/* Plugin Configuration */}
      {visiblePluginSections.length > 0 && (
        <section>
          <h4 className="text-chariot-text-primary mb-2 text-sm font-medium">
            Plugin Configuration
          </h4>
          <div className="flex flex-col gap-2">
            {visiblePluginSections.map(([pluginName, config]) => (
              <CollapsiblePluginConfig
                key={pluginName}
                pluginName={pluginName}
                label={config.label}
                params={config.params}
                values={pluginConfig}
                onChange={handlePluginConfigChange}
              />
            ))}
          </div>
        </section>
      )}

      {/* Target Selection */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-chariot-text-primary text-sm font-medium">
            Targets{' '}
            <span className="text-chariot-text-tertiary text-xs font-normal">
              (optional — leave empty for preseed scan)
            </span>
          </h4>
          {selectedTargetMap.size > 0 && (
            <button
              type="button"
              onClick={() => setSelectedTargetMap(new Map())}
              className="text-chariot-text-secondary hover:text-chariot-text-primary text-xs transition-colors"
            >
              Clear all
            </button>
          )}
        </div>

        <SearchBar
          size="sm"
          className="h-fit w-full"
          placeholder="Search domains..."
          value={targetSearch}
          onChange={setTargetSearch}
        />
        <ul
          ref={targetListRef}
          className="border-chariot-stroke mt-1 max-h-[300px] overflow-auto rounded border"
        >
          {targetsStatus === 'pending' && targetOptions.length === 0 ? (
            <li className="flex items-center justify-center py-4">
              <Loader className="size-5" isLoading />
            </li>
          ) : targetOptions.length === 0 ? (
            <li className="text-chariot-text-secondary px-3 py-3 text-xs">
              No domain or TLD assets found for this client.
            </li>
          ) : (
            targetOptions.map(option => {
              const isSelected = selectedTargetMap.has(option.value);
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    className={twMerge(
                      'hover:bg-chariot-background-secondary flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                      isSelected && 'bg-chariot-background-secondary'
                    )}
                    onClick={() => {
                      setSelectedTargetMap(prev => {
                        const next = new Map(prev);
                        if (next.has(option.value)) {
                          next.delete(option.value);
                        } else {
                          next.set(option.value, option.label);
                        }
                        return next;
                      });
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      className="pointer-events-none shrink-0"
                    />
                    <span className="text-chariot-text-primary truncate">
                      {option.label}
                    </span>
                  </button>
                </li>
              );
            })
          )}
          {isFetchingNextPage && (
            <li className="flex items-center justify-center py-2">
              <Loader className="size-4" isLoading />
            </li>
          )}
        </ul>

        {selectedTargetMap.size > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {Array.from(selectedTargetMap).map(([key, label]) => (
              <DashedTag
                key={key}
                icon={<XCircleIcon className="size-5" />}
                className="text-chariot-text-secondary py-1"
                size="sm"
                onClick={() => removeTarget(key)}
                label={label}
              />
            ))}
          </div>
        )}
      </section>

      {/* Run Controls */}
      <div className="border-chariot-stroke flex items-center justify-between border-t pt-4">
        <p className="text-chariot-text-secondary text-sm">
          {isPreseed ? (
            <>
              Preseed scan with{' '}
              <span className="font-medium">{effectivePluginCount}</span>{' '}
              plugin
              {effectivePluginCount !== 1 ? 's' : ''}
            </>
          ) : (
            <>
              Domain discovery on{' '}
              <span className="font-medium">{selectedTargetMap.size}</span>{' '}
              target
              {selectedTargetMap.size !== 1 ? 's' : ''} with{' '}
              <span className="font-medium">{effectivePluginCount}</span>{' '}
              plugin
              {effectivePluginCount !== 1 ? 's' : ''}
            </>
          )}
        </p>
        <button
          type="button"
          onClick={handleRun}
          disabled={isPending}
          className="flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
        >
          <PlayIcon className="size-4" />
          {isPending ? 'Running...' : 'Run Discovery'}
        </button>
      </div>
    </div>
  );
};
